<template>
  <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-xs">
    <div v-if="decidedDecision" class="flex flex-col gap-3" data-test="decided">
      <div class="flex items-center justify-between gap-3">
        <p class="flex items-center gap-2 text-sm font-semibold" :class="DECIDED_CLASSES[decidedDecision]">
          <CheckCircleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
          {{ t(`decided.${decidedDecision}`) }}
        </p>
        <slot name="header-action" />
      </div>
      <PrimaryButton v-if="decidedDecision === 'approve'" data-test="continue" @click="$emit('continue')">
        {{ t("continue_to_handling") }}
      </PrimaryButton>
      <SecondaryButton v-else data-test="close" @click="$emit('close')">{{ t("close") }}</SecondaryButton>
    </div>

    <div v-else class="flex flex-col gap-5">
      <div>
        <!-- `header-action` : action du parent alignée sur le titre (ex. ouvrir l'avis de l'agent). -->
        <div class="mb-2 flex items-center justify-between gap-3">
          <p class="text-xs font-bold uppercase tracking-widest text-gray-500">{{ t("title") }}</p>
          <slot name="header-action" />
        </div>
        <ClaimDecisionChoices
          :selected-decision="selectedDecision"
          :recommended-decision="agentAnalysis?.verdict || null"
          @choose="select"
        />
      </div>

      <!-- Chaque formulaire reste monté une fois ouvert : passer d'une décision à l'autre
           ne perd pas ce que l'ops a saisi. -->
      <ClaimApproveForm
        v-if="visited.approve"
        v-show="selectedDecision === 'approve'"
        :agent-synthesis="agentApprovalSynthesis"
        @confirmed="confirm('approve')"
      />
      <ClaimRefuseForm
        v-if="visited.refuse"
        v-show="selectedDecision === 'refuse'"
        :agent-draft="agentRefusalDraft"
        @confirmed="confirm('refuse')"
      />
      <ClaimComplementRequestForm
        v-if="visited.complement && declaration"
        v-show="selectedDecision === 'complement'"
        :declaration="declaration"
        :agent-complement="agentComplement"
        @success="confirm('complement')"
      />
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { PrimaryButton, SecondaryButton } from "@estaly/ui";
import ClaimApproveForm from "@/components/claim/details/action/ClaimApproveForm.vue";
import ClaimComplementRequestForm from "@/components/claim/details/complement-request/ClaimComplementRequestForm.vue";
import ClaimDecisionChoices from "@/components/claim/details/action/ClaimDecisionChoices.vue";
import ClaimRefuseForm from "@/components/claim/details/action/ClaimRefuseForm.vue";

const DECIDED_CLASSES = {
  approve: "text-green-700",
  refuse: "text-red-700",
  complement: "text-yellow-800",
};

export default {
  name: "ClaimDecisionPanel",

  components: {
    CheckCircleIcon,
    PrimaryButton,
    SecondaryButton,
    ClaimApproveForm,
    ClaimComplementRequestForm,
    ClaimDecisionChoices,
    ClaimRefuseForm,
  },

  setup() {
    const { t } = useI18n();
    return { t, DECIDED_CLASSES };
  },

  props: {
    claim: {
      type: Object,
      required: true,
    },
    // Avis de l'agent général : son verdict est marqué d'un badge, sa synthèse accompagne
    // l'approbation et son motif pré-remplit le refus.
    agentAnalysis: {
      type: Object,
      default: null,
    },
    // Décision déjà confirmée : le panneau passe en lecture seule pour qu'on ne tranche
    // pas deux fois. Portée par le parent, elle survit à un changement d'onglet.
    decidedDecision: {
      type: String,
      default: null,
    },
    // Décision dont le formulaire s'ouvre d'office ; les choix restent là pour trancher autrement.
    initialDecision: {
      type: String,
      default: null,
    },
  },

  emits: ["decided", "continue", "close"],

  data() {
    return {
      selectedDecision: this.initialDecision,
      visited: {
        approve: false,
        refuse: false,
        complement: false,
        ...(this.initialDecision && { [this.initialDecision]: true }),
      },
    };
  },

  computed: {
    declaration() {
      return this.claim.declarations?.[0] || null;
    },
    agentApprovalSynthesis() {
      return this.agentAnalysis?.verdict === "approve" ? this.agentAnalysis.synthesis || [] : [];
    },
    agentRefusalDraft() {
      return this.agentAnalysis?.verdict === "refuse" ? this.agentAnalysis.refusalDraft || null : null;
    },
    agentComplement() {
      return this.agentAnalysis?.verdict === "complement" ? this.agentAnalysis.complement || null : null;
    },
  },

  methods: {
    select(decision) {
      if (decision) this.visited[decision] = true;
      this.selectedDecision = decision;
    },
    confirm(decision) {
      this.$emit("decided", decision);
    },
  },
};
</script>

<i18n src="./ClaimDecisionPanel.i18n.json"></i18n>
